import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useLocation} from 'react-router-dom';

import useFilters from '@/hooks/useFilters';
import {merchantService} from '@/services/merchants';
import type {BusinessFee} from '@/types';
import type {AfriexQueryParams} from '@/types/AfriexQueryParams';
import {logRejection} from '@/utils/logRejection';

import type {FeeListProps} from './types';

type FeeListScope = {
  /** Restrict the list to fees scoped to this enterprise (USERID_EQUALS_<id> tag). */
  businessId?: string;
  /** Restrict the list to organization default fees (no BUSINESSID or USERID tag). */
  isGlobal?: boolean;
  /**
   * When false, the hook will not fetch. Use this to defer the request until a
   * required dependency (e.g. the businessId) is available, so we never fetch
   * and momentarily render the unscoped list.
   */
  enabled?: boolean;
};

const useFeeList = (scope: FeeListScope = {}): FeeListProps<BusinessFee> => {
  const {businessId, isGlobal, enabled = true} = scope;
  const [feeList, setFeeList] = useState<BusinessFee[]>([]);
  const [fullFeeList, setFullFeeList] = useState<BusinessFee[]>([]);
  // Non-zero when any fee exists, so the UI can tell "genuinely no fees" apart
  // from "fees exist but are all scoped to a merchant" and message the empty
  // state accordingly.
  const [rawFeeCount, setRawFeeCount] = useState<number>(0);
  // Start in the loading state while disabled so consumers show a skeleton
  // (not an empty list) until the fetch is allowed to run.
  const [isLoading, setIsLoading] = useState<boolean>(!enabled);
  const [itemTotal, setItemTotal] = useState<number>(0);
  const [serverTotal, setServerTotal] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  // Track the last-fetched page (0-indexed, matching the params) so an action
  // (create/update a fee) can refetch and land the admin back on the same page
  // instead of resetting to page 1.
  const currentPageRef = useRef<number>(0);
  // 1-indexed page synced back to Pagination after a direct/post-action fetch.
  const [displayPage, setDisplayPage] = useState<number>(1);
  const [isPageReset, setIsPageReset] = useState<boolean>(false);
  const [error, setError] = useState<unknown>(null);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const status = queryParams.get('status') as AfriexQueryParams['status'];
  const [currentFilters, setCurrentFilters] = useState<AfriexQueryParams>({});
  const [filtersToApply, setFiltersToApply] = useState<AfriexQueryParams>({});
  const [filterCount, setFilterCount] = useState(0);

  const {applyFilters} = useFilters(setCurrentFilters);

  // The `status` URL param is layered onto the filters the request is built
  // from rather than mirrored into `currentFilters` state by an effect. That
  // effect ran after the first render and always handed `currentFilters` a
  // fresh object identity, which recreated the fetch callback and fired a
  // second, identical request on every mount. `currentFilters` now holds only
  // the filters the admin picked, so its identity is stable and this memo is
  // stable with it.
  const effectiveFilters = useMemo(
    () => (status ? {...currentFilters, status} : currentFilters),
    [currentFilters, status],
  );

  const fetchAllFeeList = useCallback(
    async (params: AfriexQueryParams) => {
      // Defer until the scope is ready (e.g. businessId resolved) so we never
      // fetch and render the unscoped list.
      if (!enabled) {
        return;
      }
      try {
        setError(null);
        setIsLoading(true);

        //use page from params or default to 1
        const requestedPage = params.page || 0;
        const page = requestedPage + 1;
        if (businessId) {
          //the BE filters by businessId and returns every fee for it unpaged,
          //so page here
          const feeData = await merchantService.getBusinessFees({
            limit: 100,
            businessId,
          });
          const scopedList = feeData.data;
          setRawFeeCount(scopedList.length);
          //set full list and total based on the scoped result
          setFullFeeList(scopedList);
          setServerTotal(scopedList.length);
          setItemTotal(scopedList.length);
          //calculate start and end indices for the requested page
          const startIndex = (page - 1) * 10;
          const endIndex = startIndex + 10;
          //slice scopedList to get 10 items for the current page
          setFeeList(scopedList.slice(startIndex, endIndex));
        } else {
          const feeData = await merchantService.getBusinessFees({
            page: requestedPage,
            limit: params.limit ?? 10,
            ...(isGlobal ? {isOrgDefault: true} : {}),
          });
          setRawFeeCount(feeData.total);
          // No org-default fees: check whether any fee exists at all.
          if (isGlobal && !feeData.total) {
            const allFees = await merchantService.getBusinessFees({limit: 1});
            setRawFeeCount(allFees.total);
          }
          setFullFeeList(feeData.data);
          setFeeList(feeData.data);
          setServerTotal(feeData.total);
          setItemTotal(feeData.total);
        }
        //remember the requested page so a post-action refetch stays on it
        currentPageRef.current = requestedPage;
        setDisplayPage(page);
        setPage(page);
      } catch (e) {
        setError(e);
      } finally {
        setIsLoading(false);
      }
    },
    // The endpoint is scoped by businessId or isGlobal; `effectiveFilters` is what
    // refetches the list when the admin (or the URL) changes a filter.
    [effectiveFilters, businessId, isGlobal, enabled],
  );

  // `FeeListProps.fetchList` is `(params) => void` (types.ts) and is handed to
  // Pagination's `onPageChange`, which is also `=> void`, so adapt at the
  // boundary rather than widening either contract. `fetchAllFeeList` catches
  // into `error` state, so this handler only fires if that catch or the finally
  // itself throws. `useCallback` keeps the identity stable.
  const fetchList = useCallback(
    (params: AfriexQueryParams) => {
      fetchAllFeeList(params).catch(logRejection('Failed to load fee list'));
    },
    [fetchAllFeeList],
  );

  // Re-run the fetch for the page the admin is currently on. Used after a fee
  // is created/updated so the list refreshes in place (mirrors the invites
  // screen's post-action behavior) instead of a full page reload.
  const refetchCurrentPage = useCallback(async () => {
    await fetchAllFeeList({page: currentPageRef.current});
  }, [fetchAllFeeList]);

  // Without a businessId this searches the page on screen; the fee list
  // endpoint has no name filter.
  const handleAutoComplete = (searchTerm: string) => {
    const trimmed = searchTerm.trim().toLowerCase();
    if (!trimmed) {
      setItemTotal(serverTotal);
      setFeeList(fullFeeList.slice(0, 10));
      return;
    }
    const filtered = fullFeeList.filter((item) =>
      item.name?.toLowerCase().includes(trimmed),
    );
    setItemTotal(filtered.length);
    setFeeList(filtered.slice(0, 10));
  };

  useEffect(() => {
    fetchList({});
  }, [fetchList]);

  const handleApplyFilters = useCallback(() => {
    // Layer the URL `status` onto a copy instead of mutating the state object.
    // `applyFilters` receives exactly the same filters as before; the
    // difference is that `filtersToApply` state is no longer written behind
    // React's back, so it stays owned by `selectAppliedFilters`.
    applyFilters(status ? {...filtersToApply, status} : filtersToApply);
    setIsPageReset((prev) => !prev); // Reset pagination to the first page
  }, [applyFilters, filtersToApply, status]);

  // Handle change for user manipulation
  const selectCurrentFilters = useCallback(
    (name: string, value: string | Date | undefined) => {
      setCurrentFilters((prev) => {
        const updatedFilters = {
          ...prev,
          [name]: value,
        };
        return updatedFilters;
      });
      setIsPageReset((prev) => !prev);
    },
    [],
  );

  // Handle dates for applied filters
  const selectAppliedFilters = useCallback(
    (name: string, value: string | number | Date | undefined) => {
      setFiltersToApply((prev) => {
        const updatedFilters = {
          ...prev,
          [name]: value,
        };
        // Only count non-undefined values
        const filteredCount = Object.entries(updatedFilters).filter(
          ([_, val]) => val !== undefined,
        ).length;
        setFilterCount(filteredCount);
        return updatedFilters;
      });
    },
    [],
  );

  const cleanFilters = () => {
    setCurrentFilters({});
    setFiltersToApply({});
    setFilterCount(0);
    setIsPageReset((prev) => !prev);
    if (status) {
      setCurrentFilters({status});
    }
  };

  return {
    error,
    isLoading,
    rawFeeCount,
    pageInfo: {
      total: itemTotal,
      page,
    },
    list: feeList,
    fetchList,
    refetchCurrentPage,
    displayPage,
    setPage,
    filterCount,
    isPageReset,
    currentFilters,
    filtersToApply,
    clearFilters: cleanFilters,
    selectAppliedFilters,
    handleApplyFilters,
    selectCurrentFilters,
    handleAutoComplete,
  };
};

export default useFeeList;
